import { useState, useEffect } from "react";
import api, { formatApiError, thumb } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { Star, Plus, X, Palette, Cookie, Cake, Flame, Flower2, Shirt, Scissors, Sparkles, Users, TrendingUp, Building2, Heart, ArrowRight } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

const STUDIO_IMG = "https://images.pexels.com/photos/6925173/pexels-photo-6925173.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=600";
const CATEGORIES = ["Cake Decorating", "Cookie Decorating", "Art & Painting", "Candle Making", "Floral Design", "Fiber Arts", "Crafts", "Other"];

const TEACH_TOPICS = [
  { icon: Palette, label: "Painting" },
  { icon: Cookie, label: "Cookie Decorating" },
  { icon: Cake, label: "Cake Decorating" },
  { icon: Flame, label: "Candle Making" },
  { icon: Flower2, label: "Floral Design" },
  { icon: Shirt, label: "Fiber Arts" },
  { icon: Scissors, label: "Crafts" },
  { icon: Sparkles, label: "Other Creative Workshops" },
];

const WHY_TEACH = [
  { icon: Users, title: "Reach new students", desc: "Get in front of a built-in community of curious, creative locals." },
  { icon: TrendingUp, title: "Grow your business", desc: "Turn your craft into new income and lasting customer relationships." },
  { icon: Building2, title: "Teach in a beautiful creative studio", desc: "A fully-equipped, light-filled space designed for making." },
  { icon: Heart, title: "Join the Cake Society community", desc: "Become part of a supportive circle of makers and mentors." },
];

function fmt(d) {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

const inputClass = "w-full px-4 py-3 border-2 border-[#121212] bg-white text-[#121212] placeholder:text-[#121212]/40 focus:outline-none";
const Field = ({ label, children }) => (
  <div><label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2 text-[#121212]">{label}</label>{children}</div>
);

export default function Studio() {
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({
    class_name: "", class_description: "", category: "Cake Decorating",
    expected_attendance: 8, instagram: "", email: "", notes: "",
  });
  const [photoUrls, setPhotoUrls] = useState([""]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [settings, setSettings] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data || {})).catch(() => {});
    const today = fmt(new Date());
    api.get("/classes").then(({ data }) => setUpcoming(
      (data || []).filter((c) => c.category === "Guest Creator" && (c.date || "") >= today)
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    )).catch(() => {});
  }, []);

  const submit = async () => {
    if (!date) { toast.error("Pick a preferred date"); return; }
    if (!form.class_name || !form.class_description || !form.email) { toast.error("Fill in class name, description, and email"); return; }
    setBusy(true);
    try {
      await api.post("/creator-applications", {
        preferred_date: fmt(date),
        class_name: form.class_name,
        class_description: form.class_description,
        category: form.category,
        expected_attendance: Number(form.expected_attendance),
        instagram: form.instagram,
        email: form.email,
        photo_urls: photoUrls.filter(Boolean),
        notes: form.notes,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="application-success" className="pt-40 pb-24 fade-in text-center bg-[#F2E9D8]">
        <div className="max-w-xl mx-auto px-6">
          <Star className="mx-auto text-[#C6FF33]" size={48} fill="#C6FF33" />
          <h1 className="font-serif font-black text-5xl text-[#121212] mt-6">Thanks for applying.</h1>
          <p className="mt-4 text-[#121212]/80">Our team will review your Guest Creator proposal and reach out shortly with onboarding and scheduling details.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="studio-page" className="fade-in bg-[#F2E9D8]">
      {/* HERO */}
      <section className="pt-32 pb-16 lg:pt-44 lg:pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-5">Creative Workshops at the Studio</p>
            <h1 data-testid="studio-title" className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl text-[#121212] leading-[0.95]">
              Guest <span className="lime-swipe italic">Creators</span> at Cake Society.
            </h1>
            <p className="mt-6 text-xl text-[#121212] font-medium">A place where local creators teach, connect, and inspire.</p>
            <p className="mt-6 text-lg text-[#121212]/80 leading-relaxed max-w-xl">
              Cake Society opens its studio to talented local creators for a rotating lineup of hands-on workshops.
              From painting and cookies to florals, candles, crafts, cake decorating, and more, our Guest Creator
              classes bring the community together through creative experiences.
            </p>
            <p className="mt-4 text-lg text-[#121212]/80 leading-relaxed max-w-xl">
              Each workshop is different because every creator brings their own style, skill, and point of view.
            </p>
            <p className="mt-4 text-lg text-[#121212]/80 leading-relaxed max-w-xl">
              Classes are scheduled throughout the month based on studio and creator availability, with new workshops added regularly.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#apply" data-testid="hero-apply-link" className="group inline-flex items-center gap-2 px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all">
                Apply to Teach <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border-2 border-[#121212]">
              <img src={thumb(settings.creator_mondays_image || STUDIO_IMG, 600)} alt="Guest Creators studio" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING GUEST CREATOR CLASSES */}
      <section data-testid="upcoming-guest-creators" className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 pb-4">
        <p className="text-xs tracking-[0.3em] uppercase font-bold mb-6 flex items-center gap-2"><Star size={14} fill="#C6FF33" stroke="#121212" /> Upcoming Guest Creator Classes</p>
        {upcoming.length === 0 ? (
          <div data-testid="upcoming-coming-soon" className="border-2 border-dashed border-[#121212]/25 p-8 text-[#121212]/70">
            New Guest Creator workshops are added regularly — check back soon, or <a href="#apply" className="font-bold underline">apply to teach</a> one yourself.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcoming.map((c) => (
              <a key={c.id} href={`/classes/${c.slug || c.id}`} data-testid={`upcoming-gc-${c.id}`} className="group bg-white border-2 border-[#121212] hover:-translate-y-1 transition-transform flex flex-col">
                <div className="aspect-[4/3] overflow-hidden border-b-2 border-[#121212]">
                  <img src={thumb(c.image_url, 600)} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="self-start px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-bold bg-[#2D6BFF] text-white">Guest Creator</span>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212] mt-3">{c.date}{c.time ? ` · ${c.time}` : ""}</p>
                  <h3 className="font-serif font-black text-xl text-[#121212] mt-1 leading-tight">{c.title}</h3>
                  <div className="mt-4 pt-4 border-t border-[#121212]/10 flex items-center justify-between">
                    <span className="font-serif font-black text-2xl text-[#121212]">${c.price}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold group-hover:gap-2.5 transition-all">Book <ArrowRight size={13} /></span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* WANT TO TEACH + APPLICATION FORM */}
      <section id="apply" className="bg-[#121212] text-[#F2E9D8] py-20 lg:py-28 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33] mb-4">Want to teach at Cake Society?</p>
          <h2 className="font-serif font-black text-4xl lg:text-5xl leading-[0.95]">Are you a local creator?</h2>
          <p className="mt-6 text-[#F2E9D8]/80 text-lg leading-relaxed">
            We'd love to hear from you! Submit your class proposal below. We review every application
            and contact approved creators with onboarding and scheduling information.
          </p>
        </div>

        <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16 mt-12">
          <div className="bg-white border-2 border-[#121212] p-8 text-left">
            <h3 className="font-serif font-black text-3xl text-[#121212]">Submit a class proposal</h3>
            <p className="text-[#121212]/70 text-sm mt-2">All fields are reviewed by the Cake Society team. We'll be in touch.</p>

            <div className="mt-6 space-y-5">
              <Field label="Preferred date">
                <div className="border-2 border-[#121212] p-2 inline-block">
                  <Calendar mode="single" selected={date} onSelect={setDate}
                    classNames={{
                      caption_label: "text-sm font-bold text-[#121212]",
                      nav_button: "h-7 w-7 bg-transparent p-0 opacity-100 text-[#121212] hover:opacity-60",
                    }}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    data-testid="application-calendar" />
                </div>
                <p className="text-xs text-[#121212]/60 mt-2">Guest Creator workshops are scheduled based on studio availability and the needs of each class. Dates may include weekday evenings, Fridays, Sundays, and other available studio times.</p>
              </Field>
              <Field label="Class name">
                <input data-testid="class-name" value={form.class_name} onChange={(e) => setForm({...form, class_name: e.target.value})} className={inputClass} />
              </Field>
              <Field label="Class description">
                <textarea data-testid="class-description" rows={3} value={form.class_description} onChange={(e) => setForm({...form, class_description: e.target.value})} className={inputClass + " resize-none"} />
              </Field>
              <Field label="Category">
                <select data-testid="category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className={inputClass}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Expected attendance">
                <input data-testid="expected-attendance" type="number" min={1} max={20} value={form.expected_attendance} onChange={(e) => setForm({...form, expected_attendance: e.target.value})} className={inputClass} />
              </Field>
              <Field label="Instagram handle">
                <input data-testid="instagram" placeholder="@yourname" value={form.instagram} onChange={(e) => setForm({...form, instagram: e.target.value})} className={inputClass} />
              </Field>
              <Field label="Email">
                <input data-testid="email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={inputClass} />
              </Field>
              <Field label="Photos of your work (upload from your phone)">
                <div className="space-y-3">
                  {photoUrls.map((u, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <ImageUpload
                          publicMode
                          label=""
                          testid={`photo-${i}`}
                          value={u}
                          onChange={(url) => setPhotoUrls(photoUrls.map((p, j) => j === i ? url : p))}
                        />
                      </div>
                      {photoUrls.length > 1 && <button type="button" data-testid={`remove-photo-${i}`} onClick={() => setPhotoUrls(photoUrls.filter((_, j) => j !== i))} className="p-2 border-2 border-[#121212] mt-7"><X size={16} /></button>}
                    </div>
                  ))}
                  <button type="button" data-testid="add-photo" onClick={() => setPhotoUrls([...photoUrls, ""])} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold"><Plus size={14} /> Add another</button>
                </div>
              </Field>
              <Field label="Special requirements / notes">
                <textarea data-testid="notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className={inputClass + " resize-none"} />
              </Field>
            </div>

            <button data-testid="submit-application" onClick={submit} disabled={busy} className="mt-8 w-full px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all">
              {busy ? "Submitting…" : "Submit Class Proposal"}
            </button>
          </div>
        </div>
      </section>

      {/* WHAT CAN YOU TEACH */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-28">
        <div className="mb-14 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">So many possibilities</p>
          <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212]">What can you <span className="lime-swipe italic">teach?</span></h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TEACH_TOPICS.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={t.label} data-testid={`teach-topic-${i}`} className="group p-7 bg-white border-2 border-[#121212] hover:bg-[#C6FF33] transition-all flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-[#F2E9D8] border-2 border-[#121212] flex items-center justify-center group-hover:bg-white transition-all">
                  <Icon size={26} className="text-[#121212]" />
                </div>
                <p className="mt-4 font-serif font-bold text-lg text-[#121212] leading-tight">{t.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY TEACH */}
      <section className="bg-[#121212] text-[#F2E9D8] py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="mb-14">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33] mb-4">The good stuff</p>
            <h2 className="font-serif font-black text-5xl lg:text-6xl">Why teach at Cake Society?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_TEACH.map((w, i) => {
              const Icon = w.icon;
              return (
                <div key={i} data-testid={`why-teach-${i}`} className="p-7 border-2 border-[#F2E9D8]/20 hover:border-[#C6FF33] transition-all">
                  <div className="w-12 h-12 bg-[#C6FF33] border-2 border-[#121212] flex items-center justify-center">
                    <Icon size={22} className="text-[#121212]" />
                  </div>
                  <h3 className="mt-5 font-serif font-black text-xl leading-tight">{w.title}</h3>
                  <p className="mt-2 text-[#F2E9D8]/70 text-sm leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <a href="#apply" data-testid="why-apply-link" className="inline-flex items-center gap-3 px-8 py-4 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.25em] uppercase font-bold transition-all">
              Apply to teach <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
